'use strict';

var _ = require('lodash');
var PooledSeasonalityChartDataTransformationService = function PooledSeasonalityChartDataTransformationService(momentService, seasonalityConstants, reviewConstants, reviewCalendarService) {
  var readingTimeRegex = /(\d+)d \d+h \d+m/;
  function computeAverageDemand(demandProfile, selectedRtps) {
    var readingDayDemands = demandProfile.properties.profile.readingDayDemands;
    var demandRtpMap = {};
    _.forEach(selectedRtps, function (rtp) {
      var demandData = {};
      var remainingUntruncatedResHolds = _.chain(readingDayDemands).filter(function (readingDayDemand) {
        var readingDay = readingDayDemand.readingTimeInterval.match(readingTimeRegex);
        return readingDay[1] === rtp;
      }).map(function (demand) {
        return demand.averageRemainingUntruncatedResHold;
      }).value();
      demandData.actual = _.mean(remainingUntruncatedResHolds);
      _.set(demandRtpMap, rtp, demandData);
    });
    var maxAvgDemand = _.maxBy(_.values(demandRtpMap), 'actual');
    _.keys(demandRtpMap).forEach(function (key) {
      var data = _.get(demandRtpMap, key);
      var scale = data.actual > 0 ? setScale(maxAvgDemand.actual, data.actual) : 0;
      _.set(data, 'scale', scale);
      _.set(demandRtpMap, key, data);
    });
    return demandRtpMap;
  }
  function getScaledWoyIndices(indexProfile) {
    var profileIndexes = indexProfile.properties.profile.indexes;
    var maxIndexValue = _.maxBy(profileIndexes, 'index');
    return _.reduce(profileIndexes, function (woyIndices, index) {
      return woyIndices.concat({
        actual: index.index,
        scale: setScale(maxIndexValue.index, index.index)
      });
    }, []);
  }
  function getWoyIndices(indexProfile) {
    var storedWoyIndices = indexProfile[0].properties.profile.indexes;
    var woyIndices = {};
    _.map(storedWoyIndices, function (woyIndex) {
      _.set(woyIndices, woyIndex.woy, woyIndex);
    });
    return woyIndices;
  }
  function setScale(maxValue, value) {
    if (maxValue === 0) {
      return 0;
    }
    return 100 - (maxValue - value) / maxValue * 100;
  }
  function barsToOutlierDoc(carrier, market, selectedBars, cabins, dowGroup) {
    var outlierDocs = [];
    var origin = market[0];
    var destination = market[1];
    _.forEach(cabins, function (cabin) {
      var outliers = _.reduce(selectedBars, function (aggregator, bar) {
        return aggregator.concat({
          carrier: carrier,
          origin: origin,
          destination: destination,
          cabin: cabin,
          significantPOS: bar.spos,
          departureDate: bar.d1.format(momentService.FORMAT.DATE_TIME_ISO_WITHOUT_SECONDS),
          isRegularOutlier: bar.isRegularOutlier,
          isMappedOutlier: bar.isMappedOutlier,
          dowGroup: dowGroup
        });
      }, []);
      outlierDocs = _.union(outliers, outlierDocs);
    });
    return outlierDocs;
  }
  function convertToDisplayedCalendar(calendar, contextData) {
    var displayedCalendar = _.pick(calendar, ['calendarName', 'specialEvents', 'seasons', 'status', 'origin', 'destination', 'seasonalityOverrides', 'dowGroups']);
    var lastProcessedMoment = contextData.searchCriteria.lastProcessedDateMoment.startOf('day');
    var startDate = lastProcessedMoment.subtract(52, 'weeks');
    displayedCalendar.calendarEntryStatus = seasonalityConstants.CALENDAR_STATUSES.PUBLISHED;
    displayedCalendar.calendarStatus = displayedCalendar.status;
    displayedCalendar.market = _.join([displayedCalendar.origin, displayedCalendar.destination], '-');
    displayedCalendar.startDepartureDate = startDate;
    displayedCalendar.endDepartureDate = lastProcessedMoment;
    if (displayedCalendar.specialEvents && displayedCalendar.specialEvents.length > 0) {
      _.forEach(displayedCalendar.specialEvents, function (event) {
        event.startDate = momentService.of(event.startDepartureDate, momentService.FIELD.DEPARTURE_DATE);
        event.endDate = momentService.of(event.endDepartureDate, momentService.FIELD.DEPARTURE_DATE);
      });
    }
    displayedCalendar.dowGroup = determineDowGroup(contextData);
    var storedOverrides = displayedCalendar.seasonalityOverrides || [];
    displayedCalendar.overrides = _.values(_.keyBy(storedOverrides, overrideUniqKey));
    displayedCalendar.dowGroupList = getDowGroups(displayedCalendar.dowGroups);
    displayedCalendar.selectedDow = _.filter(displayedCalendar.dowGroupList, function (dow) {
      return dow.id === displayedCalendar.dowGroup;
    })[0];
    return displayedCalendar;
  }
  function overrideUniqKey(override) {
    return _.chain(override).pick(['cabin', 'dowGroup', 'marketSpecialEvents', 'significantPOS', 'weekNo', 'year']).values().compact().join('-').value();
  }
  function getDowGroups(dowGroups) {
    return _.map(dowGroups, function (dowGroup) {
      return {
        id: dowGroup.group,
        label: dowGroup.dow.join(',')
      };
    });
  }
  function determineDowGroup(contextData) {
    var incomingDowGroup = contextData.searchCriteria.dowGroup;
    var dowGroup;
    if (incomingDowGroup === reviewConstants.DOW_GROUP_ALL) {
      _.mapKeys(reviewConstants.DAY_OF_WEEK_MAPPING, function (value, key) {
        if (value === contextData.searchCriteria.departureDate.day()) {
          dowGroup = reviewCalendarService.getUsableDowGroup(key);
          return false;
        }
      });
    } else {
      dowGroup = incomingDowGroup.split('_')[0];
    }
    return dowGroup || 'M';
  }
  function createDemandTransparencyAttributes(contextData, marketRtps, flowType) {
    var market = contextData.searchCriteria.market;
    var classGroup = contextData.searchCriteria.classGroup;
    var dowGroup = determineDowGroup(contextData);
    var daysToDeparture = contextData.searchCriteria.daysToDeparture;
    var spos = contextData.searchCriteria.spos;
    var selectedCabin, selectedDow, selectedRtp;
    _.forEach(marketRtps, function (rtp) {
      if (parseInt(rtp) >= daysToDeparture) {
        selectedRtp = rtp;
        return false;
      }
    });
    var classGroupMapping = reviewCalendarService.getCabinClassGroups(market);
    _.keys(classGroupMapping).forEach(function (cabin) {
      var classGroups = _.get(classGroupMapping, cabin);
      var selectedObject = _.find(classGroups, {
        classGroup: classGroup
      });
      if (!_.isUndefined(selectedObject)) {
        selectedCabin = cabin;
        return false;
      }
    });
    var dowGroupMapping = reviewConstants.DOW_DISPLAY_TO_USAGE;
    _.keys(dowGroupMapping).forEach(function (dow) {
      var usableDow = _.get(dowGroupMapping, dow);
      if (_.isEqual(dowGroup, usableDow)) {
        selectedDow = dow;
        return false;
      }
    });
    return {
      selectedFlowType: _.capitalize(flowType),
      selectedCabin: selectedCabin,
      significantPOS: spos,
      selectedDow: selectedDow,
      selectedClassGroup: classGroup,
      selectedRtp: selectedRtp,
      contextData: contextData
    };
  }
  return {
    computeAverageDemand: computeAverageDemand,
    getScaledWoyIndices: getScaledWoyIndices,
    getWoyIndices: getWoyIndices,
    barsToOutlierDoc: barsToOutlierDoc,
    convertToDisplayedCalendar: convertToDisplayedCalendar,
    createDemandTransparencyAttributes: createDemandTransparencyAttributes
  };
};
PooledSeasonalityChartDataTransformationService.$inject = ['MomentService', 'SeasonalityConstants', 'PooledSeasonalityReviewConstants', 'PooledReviewCalendarService'];
module.exports = PooledSeasonalityChartDataTransformationService;